import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import Loading from '@components/common/elements/Loading';
import PageWrapper from '@components/common/layout/PageWrapper';
import { DEFAULT_FORMAT, STATUS_ACTIVE, TIME_FORMAT_DISPLAY } from '@constants';
import apiConfig from '@constants/apiConfig';
import { daysOfWeekTimeWork, deviceKindOptions, deviceSubKindOptions, statusOptions } from '@constants/masterData';
import useFetch from '@hooks/useFetch';
import useTranslate from '@hooks/useTranslate';
import { commonMessage } from '@locales/intl';
import { showSucsessMessage } from '@services/notifyService';
import { getDetailRestaurantSuccess } from '@store/actions/restaurant';
import { IconDeviceDesktop } from '@tabler/icons-react';
import { calcCoefficient } from '@utils';
import {
    Button,
    Card,
    Descriptions,
    Divider,
    Flex,
    Form,
    Input,
    Modal,
    QRCode,
    Tag,
    Typography,
} from 'antd';
import moment from 'moment';
const { Text } = Typography;
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
import StoreSiteSettingForm from './StoreSiteSettingForm';

const RestaurantStoreSitePage = () => {
    const dispatch = useDispatch();
    const translate = useTranslate();
    const { restaurantId } = useParams();
    const [isGetDetailLoading, setIsGetDetailLoading] = useState(false);
    const [dataDetail, setDataDetail] = useState({});
    const [isChangedFormValues, setIsChangedFormValues] = useState(false);
    const [openTime, setOpenTime] = useState('');
    const [closeTime, setCloseTime] = useState('');
    const [openModalQrCode, setOpenModalQrCode] = useState(false);
    const [qrData, setQrData] = useState({});
    const [wifiSsid, setWifiSsid] = useState('');
    const [wifiPass, setWifiPass] = useState('');
    const [finalQrValue, setFinalQrValue] = useState('');
    const statusOpt = translate.formatKeys(statusOptions, ['label']);
    const deviceKindOpt = translate.formatKeys(deviceKindOptions, ['label']);
    const deviceSubKindOpt = translate.formatKeys(deviceSubKindOptions, ['label']);
    const [rawGeneralTimeWork, setRawGeneralTimeWork] = useState(null);

    const { execute: executeGet, loading } = useFetch(apiConfig.restaurant.getById, {
        immediate: false,
    });

    const { execute: update, loading: loadingUpdate } = useFetch(apiConfig.restaurant.updateByCustomer);

    const { execute: executeGetRequestCode, loading: loadingGetRequestCode } = useFetch(apiConfig.device.requestQrCode);

    const getDetail = (restaurantId) => {
        setIsGetDetailLoading(true);
        executeGet({
            pathParams: { id: restaurantId },
            onCompleted: (response) => {
                const settings = JSON.parse(response.data.settings || '{}');
                setRawGeneralTimeWork(settings.general_time_work);

                const mappedData = getDataDetailMapping(response.data);
                setDataDetail(mappedData);
                setIsGetDetailLoading(false);
            },
            onError: () => {
                setIsGetDetailLoading(false);
            },
        });
    };

    const prepareUpdateData = (settingObj) => {
        const { dayOffs, ...timeWorkData } = settingObj.time_work;
        const timeWorkObj = {};

        if (settingObj?.enable_time_work && timeWorkData) {
            Object.keys(timeWorkData).forEach((timeWorkKey) => {
                timeWorkObj[timeWorkKey] = timeWorkData[timeWorkKey]?.map(
                    (timeWorkItem) => `${timeWorkItem.from.format('HH:mm')}-${timeWorkItem.to.format('HH:mm')}`,
                ) || [];
            });
        }

        const newSettingObj = {
            ...dataDetail.settings,
            ...settingObj,
            time_work: settingObj?.enable_time_work ? {
                ...timeWorkObj,
                dayOffs,
                close: closeTime,
                open: openTime,
            } : null,
            general_time_work: rawGeneralTimeWork,
            deliver_setting: {
                ...settingObj.deliver_setting,
                distance: (settingObj.deliver_setting.distance || []).map(({ km, min_money, ship_money }) => ({
                    km,
                    min_money: calcCoefficient(min_money, 0.01),
                    ship_money: calcCoefficient(ship_money, 0.01),
                })),
            },
        };

        if (newSettingObj.paymentDeliver) {
            newSettingObj.paymentDeliver = newSettingObj.paymentDeliver.map((pmt) => ({ code: pmt }));
        }
        delete newSettingObj?.enable_time_work;
        return { ...dataDetail, settings: JSON.stringify(newSettingObj) };
    };

    const getTimeWorksData = (timeWorkObj) => {
        let timeWork = {};
        if (timeWorkObj && timeWorkObj.open && timeWorkObj.close) {
            timeWork = {
                open: moment(timeWorkObj.open, TIME_FORMAT_DISPLAY),
                close: moment(timeWorkObj.close, TIME_FORMAT_DISPLAY),
            };
        }
        return timeWork;
    };

    const getTimeWorkTabletRandomData = (timeWork) => {
        let timeWorkData = { dayOffs: [] };
        if (timeWork) {
            daysOfWeekTimeWork.forEach((day) => {
                timeWorkData[day.value] = timeWork[day.value].map((timeWorkItem) => {
                    const timeWorkStr = timeWorkItem.split('-');
                    return {
                        from: moment(timeWorkStr[0], TIME_FORMAT_DISPLAY),
                        to: moment(timeWorkStr[1], TIME_FORMAT_DISPLAY),
                    };
                });
            });
            timeWorkData.dayOffs = timeWork.dayOffs;
        }
        else {
            daysOfWeekTimeWork.forEach((day) => {
                timeWorkData[day.value] = [
                    { from: moment('00:00', TIME_FORMAT_DISPLAY), to: moment('00:00', TIME_FORMAT_DISPLAY) },
                    { from: moment('00:00', TIME_FORMAT_DISPLAY), to: moment('00:00', TIME_FORMAT_DISPLAY) },
                ];
            });
        }
        return timeWorkData;
    };

    const getDataDetailMapping = (data) => {
        const dataDetail = { ...data };
        dataDetail.settings = JSON.parse(data.settings) || {};
        if (!dataDetail.settings.time_work) {
            dataDetail.settings.time_work = {};
        }
        if (dataDetail.settings.deliver_setting?.distance) {
            dataDetail.settings.deliver_setting.distance = dataDetail.settings.deliver_setting.distance.map(
                ({ km, min_money, ship_money }) => ({
                    km,
                    min_money: calcCoefficient(min_money, 100),
                    ship_money: calcCoefficient(ship_money, 100),
                }),
            );
        }
        const { open, close, ...rest } = dataDetail.settings?.time_work || {};
        const restGeneral = dataDetail.settings?.general_time_work || {};

        setCloseTime(close);
        setOpenTime(open);

        const hasPrivateTimeWork = Object.values(rest).some(val => Array.isArray(val) && val.length > 0);
        if (hasPrivateTimeWork) {
            dataDetail.settings.time_work = {
                ...getTimeWorkTabletRandomData(rest),
            };
            dataDetail.settings.enable_time_work = true;
        } else {
            dataDetail.settings.time_work = {
                ...getTimeWorkTabletRandomData(Object.keys(restGeneral).length ? restGeneral : ''),
            };
            dataDetail.settings.enable_time_work = false;
        }

        dataDetail.settings.general_time_work = {
            ...getTimeWorkTabletRandomData(Object.keys(restGeneral).length ? restGeneral : ''),
        };

        if (dataDetail.settings.paymentDeliver) {
            dataDetail.settings.paymentDeliver = dataDetail.settings.paymentDeliver.map((pmt) => pmt.code);
        } else {
            dataDetail.settings.paymentDeliver = [];
        }

        return dataDetail;
    };

    const onSave = (values) => {
        const updatedData = prepareUpdateData(values);
        update({
            data: updatedData,
            onCompleted: (res) => {
                showSucsessMessage(translate.formatMessage(commonMessage.updateOrderSettingSuccess), translate);
                executeGet({
                    pathParams: { id: restaurantId },
                    onCompleted: (response) => {
                        const mappedData = getDataDetailMapping(response.data);
                        setDataDetail(mappedData);
                        dispatch(getDetailRestaurantSuccess({ payload: { data: response.data } }));
                        setIsGetDetailLoading(false);
                    },
                });
            },
            onError: () => { },
        });
    };

    useEffect(() => {
        if (restaurantId) {
            getDetail(restaurantId);
        }
    }, [restaurantId]);

    const renderActions = () => {
        return [];
    };

    const getFormId = () => {
        return 'store-site-setting-form';
    };

    const handleGetQrCode = (id) => {
        executeGetRequestCode({
            params: {
                restaurantId: id,
            },
            onCompleted: (res) => {
                setQrData(res.data);
                setOpenModalQrCode(true);
            },
            onError: () => { },
        });
    };

    const formatWithRestaurantTimezone = (dateStr, settingsStr) => {
        if (!dateStr || !settingsStr) return dateStr || '';

        try {
            const settings = JSON.parse(settingsStr);
            const tz = settings?.timezone?.name;

            if (!tz) return dateStr;

            return dayjs.tz(dateStr, DEFAULT_FORMAT, tz).local().format(DEFAULT_FORMAT);
        } catch {
            return dateStr;
        }
    };
    const buildQrWithWifi = () => {
        if (!qrData?.qrcode) return '';

        const cloned = JSON.parse(JSON.stringify(qrData.qrcode));

        if (cloned['android.app.extra.PROVISIONING_WIFI_SSID']) {
            cloned['android.app.extra.PROVISIONING_WIFI_SSID'] = cloned[
                'android.app.extra.PROVISIONING_WIFI_SSID'
            ].replace('{{ssid}}', wifiSsid);
        }

        if (cloned['android.app.extra.PROVISIONING_WIFI_PASSWORD']) {
            cloned['android.app.extra.PROVISIONING_WIFI_PASSWORD'] = cloned[
                'android.app.extra.PROVISIONING_WIFI_PASSWORD'
            ].replace('{{pass}}', wifiPass);
        }

        return JSON.stringify(cloned);
    };

    if (isGetDetailLoading) {
        return <Loading />;
    }

    return (
        <PageWrapper
            loading={loading}
            routes={[{ breadcrumbName: translate.formatMessage(commonMessage.orderSettings) }]}
        >
            <div className="basic-form-page">
                <Card
                    title={
                        <Flex justify="space-between" align="center">
                            {translate.formatMessage(commonMessage.orderSettings)}
                            <Button
                                type="primary"
                                htmlType="button"
                                icon={<IconDeviceDesktop />}
                                onClick={() => {
                                    handleGetQrCode(restaurantId);
                                }}
                            />
                        </Flex>
                    }
                    bordered={false}
                    actions={renderActions()}
                >
                    <StoreSiteSettingForm
                        formId={getFormId()}
                        onSubmit={onSave}
                        settingObj={dataDetail.settings || {}}
                        setIsChangedFormValues={setIsChangedFormValues}
                        isChangedFormValues={isChangedFormValues}
                        loadingUpdate={loadingUpdate}
                    />
                </Card>
                <Modal
                    open={openModalQrCode}
                    footer={null}
                    onCancel={() => {
                        setOpenModalQrCode(false);
                        setWifiSsid('');
                        setWifiPass('');
                        setFinalQrValue('');
                    }}
                    width={qrData.qrcode ? finalQrValue ? 450 : null : 500}
                    centered
                    title={
                        qrData.qrcode
                            ? translate.formatMessage(commonMessage.scanQrCode)
                            : translate.formatMessage(commonMessage.deviceInformation)
                    }
                    maskClosable={false}

                >
                    {qrData && (
                        <>
                            {qrData.qrcode ? (
                                <Flex vertical align="center" justify="center" gap={16} style={{ width: '100%', minWidth: 300, minHeight: finalQrValue ? 378 : 'auto' }}>
                                    {!finalQrValue && (
                                        <Form layout="vertical" style={{ width: '100%' }}>
                                            <Form.Item
                                                label={translate.formatMessage(commonMessage.wifiSSID)}
                                                required
                                                style={{
                                                    marginBottom: '10px',
                                                }}
                                            >
                                                <Input
                                                    placeholder={translate.formatMessage(commonMessage.placeholderWifiSsid)}
                                                    value={wifiSsid}
                                                    onChange={(e) => setWifiSsid(e.target.value)}
                                                />
                                            </Form.Item>

                                            <Form.Item
                                                label={translate.formatMessage(commonMessage.wifiPassword)}
                                                required
                                                style={{
                                                    marginBottom: '10px',
                                                }}
                                            >
                                                <Input.Password
                                                    placeholder={translate.formatMessage(commonMessage.placeholderWifiPassword)}
                                                    value={wifiPass}
                                                    onChange={(e) => setWifiPass(e.target.value)}
                                                />
                                            </Form.Item>

                                            <Button
                                                type="primary"
                                                block
                                                onClick={() => {
                                                    const finalValue = buildQrWithWifi();
                                                    setFinalQrValue(finalValue);
                                                }}
                                            >
                                                {translate.formatMessage(commonMessage.continue)}
                                            </Button>
                                        </Form>
                                    )}

                                    {finalQrValue && <QRCode value={finalQrValue} size={400} />}
                                </Flex>
                            ) : (
                                qrData?.device && (
                                    <Descriptions column={1} bordered size="middle">
                                        <Descriptions.Item label={translate.formatMessage(commonMessage.deviceName)}>
                                            {qrData?.device.deviceName}
                                        </Descriptions.Item>

                                        <Descriptions.Item label={translate.formatMessage(commonMessage.createdDate)}>
                                            {formatWithRestaurantTimezone(
                                                qrData?.device?.createdDate,
                                                qrData?.device?.restaurantDto?.settings,
                                            )}
                                        </Descriptions.Item>

                                        <Descriptions.Item label={translate.formatMessage(commonMessage.deviceToken)}>
                                            <Typography.Text copyable>{qrData?.device.deviceToken}</Typography.Text>
                                        </Descriptions.Item>

                                        <Descriptions.Item label={translate.formatMessage(commonMessage.deviceKind)}>
                                            {
                                                deviceKindOpt?.find((item) => item.value === qrData?.device.deviceKind)
                                                    ?.label
                                            }
                                        </Descriptions.Item>

                                        <Descriptions.Item label={translate.formatMessage(commonMessage.deviceSubKind)}>
                                            {
                                                deviceSubKindOpt?.find(
                                                    (item) => item.value === qrData?.device.deviceSubKind,
                                                )?.label
                                            }
                                        </Descriptions.Item>

                                        <Descriptions.Item label={translate.formatMessage(commonMessage.status)}>
                                            <Tag
                                                color={
                                                    statusOpt?.find((item) => item.value === qrData?.device.status)
                                                        ?.color
                                                }
                                                variant="outlined"
                                            >
                                                {statusOpt?.find((item) => item.value === qrData?.device.status)?.label}
                                            </Tag>
                                        </Descriptions.Item>
                                    </Descriptions>
                                )
                            )}
                        </>
                    )}
                </Modal>
            </div>
        </PageWrapper>
    );
};

export default RestaurantStoreSitePage;
